/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import Button from '@/components/Button';
import Rating from '@/components/Rating';
import { getUserDisplayName } from '@/features/auth/helpers';
import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useFeedbackContext } from '@/features/feedbacks/contexts/FeedbackContext';

import FeedbackSubmitted from './FeedbackSubmitted';
import {
  Dialog,
  Info,
  ScoreSection,
  CommentSection,
  CommentInput,
  Actions,
} from './styles';

interface FormState {
  score: number | null;
  comment: string;
  isSubmitting: boolean;
  isSubmitted: boolean;
}

type FeedbackType = 'score' | 'comment';

const FeedbackOptions = [1, 2, 3, 4, 5];
const initialState: FormState = {
  score: null,
  comment: '',
  isSubmitting: false,
  isSubmitted: false,
};

const FeedbackModal: React.FC = () => {
  const { user } = useAuth();
  const {
    state: { isModalOpen },
    actions: { dispatchSendFeedback, dispatchCloseModal },
  } = useFeedbackContext();

  const [formState, setFormState] = React.useState<FormState>(initialState);

  const mode: FeedbackType = React.useMemo(() => {
    return user && user.gave_score_feedback ? 'comment' : 'score';
  }, [user]);

  const commentPlaceholder = React.useMemo(() => {
    let placeholder = 'Digite aqui o seu comentário';
    if (mode === 'score') {
      placeholder += ' (opcional)';
    }
    return placeholder;
  }, [mode]);

  const canSubmit = React.useMemo(() => {
    if (mode === 'score') {
      return formState.score !== null;
    }

    return !!formState.comment;
  }, [formState, mode]);

  const handleScoreChange = React.useCallback((value: number | null) => {
    setFormState(prev => ({ ...prev, score: value }));
  }, []);

  const handleCommentChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const {
        target: { value },
      } = event;
      setFormState(prev => ({ ...prev, comment: value }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { comment, score } = formState;
    setFormState(prev => ({ ...prev, isSubmitting: true }));

    await dispatchSendFeedback({ comment, score });

    setFormState(prev => ({
      ...prev,
      isSubmitting: false,
      isSubmitted: true,
    }));
  }, [dispatchSendFeedback, formState]);

  const resetState = React.useCallback(() => {
    setFormState(initialState);
  }, []);

  const onClose = React.useCallback(() => {
    dispatchCloseModal();
  }, [dispatchCloseModal]);

  return (
    <Dialog
      title="Envie seu feedback"
      onAfterOpen={resetState}
      onAfterClose={onClose}
      isOpen={isModalOpen}
    >
      {formState.isSubmitted ? (
        <FeedbackSubmitted />
      ) : (
        <>
          <Info>
            <h2>
              Olá, {getUserDisplayName(user)}. O que você está achando do
              Medflow?
            </h2>
            <p>
              Conte para gente como está sendo sua experiência e nos ajude a
              deixar o Medflow cada vez melhor. 😉
            </p>
          </Info>

          {mode === 'score' && (
            <ScoreSection>
              <h2>O quanto o Medflow contribui no seu trabalho ou estudo?</h2>
              <Rating options={FeedbackOptions} onChange={handleScoreChange} />
            </ScoreSection>
          )}

          <CommentSection>
            <h2>
              Como o Medflow poderia contribuir mais no seu trabalho ou estudo?
            </h2>
            <CommentInput
              rows={4}
              placeholder={commentPlaceholder}
              value={formState.comment}
              onChange={handleCommentChange}
            />
          </CommentSection>

          <Actions>
            <Button theme="secondary" onClick={onClose}>
              Agora não
            </Button>
            <Button
              theme="primary"
              disabled={!canSubmit}
              loading={formState.isSubmitting}
              onClick={handleSubmit}
            >
              Enviar
            </Button>
          </Actions>
        </>
      )}
    </Dialog>
  );
};

export default FeedbackModal;
